/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css } from 'lit-element';

export const inputNumberStyles = css`
  :host {
    width: 100%;
    height: auto;
    position: relative;
    display: inline-block;
    flex-direction: column;
  }
  .host-wrapper {
    width: 100%;
  }
  .input-number-label {
    color: var(--text-secondary);
    position: absolute;
    left: 0.5rem;
    width: calc(100% - 4.5rem);
    transition: var(--transition-time) all ease-in-out;
    pointer-events: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    -moz-osx-font-smoothing: grayscale;
    top: 0.5rem;
    font: var(--body-1);
    line-height: 1.5rem;
    display: flex;
  }
  :host(:not(.empty)) .input-number-label {
    top: 0.25rem;
    font: var(--body-2);
    height: 1rem;
  }

  .label-wrapper {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: fit-content;
  }
  input,
  :host([readonly]) input:focus {
    outline: none;
    border: none;
    font: var(--body-1);
    line-height: 1.25rem;
    color: var(--text-primary);
    background: rgba(var(--ui-1), var(--opacity-7));
    height: 2.5rem;
    line-height: 1rem;
    border-radius: 0.125rem 0.125rem 0rem 0rem;
    width: 100%;
    box-sizing: border-box;
    padding: 1.25rem 4.5rem 0.25rem 0.5rem;
    text-overflow: ellipsis;
    transition: var(--transition-time) all ease-in-out;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    -moz-osx-font-smoothing: grayscale;
    -webkit-appearance: none;
    border-bottom: 0.0625rem solid transparent;
    border-color: rgba(var(--ui-1), var(--opacity-4));
    opacity: 1;
  }
  :host([no-arrows]) .icon-slot {
    display: none;
  }
  :host([no-arrows]) input:focus {
    padding: 1.25rem 0.5rem 0.25rem 0.5rem;
  }
  :host([readonly]) input {
    padding: 1.25rem 0.5rem 0.25rem 0.5rem;
  }
  :host([no-arrows]:not([success]):not([error])) #clear {
    right: 0.25rem;
  }

  :host([error][no-clear]:not([readonly])) input,
  :host([success]:not([readonly])[no-clear]) input,
  :host(:focus-within) input {
    padding-right: 6.5rem;
  }
  :host([success]:not([readonly])[no-clear][no-arrows]) input {
    padding-right: 2.25rem;
  }
  :host([error]:not([readonly])[no-clear][no-arrows]) input {
    padding-right: 2.25rem;
  }
  :host(:focus-within:not([no-arrows])) input {
    padding-right: 4.5rem;
  }
  :host(:focus-within:not([no-arrows][no-arrows])) input {
    padding-right: 4.5rem;
  }
  :host([success]:focus-within:not([no-arrows][no-clear])) input {
    padding-right: 6.5rem;
  }
  :host([error]:focus-within:not([no-arrows][no-clear])) input {
    padding-right: 6.5rem;
  }
  :host(:hover:not([disabled]):not([readonly]):not([no-clear]):not([error]):not([success])) input {
    padding-right: 2.25rem;
  }
  :host(:hover:not([disabled]):not([readonly]):not([no-clear]):not([error]):not([success]):not([no-arrows]))
    input {
    padding-right: 6.5rem;
  }
  :host([error][readonly]) input,
  :host([success][readonly]) input {
    padding-right: 6.5rem;
  }
  :host(:hover[no-arrows][no-clear]:not(.empty):not([disabled]):not([readonly]):not([condensed]))
    input,
  :host([error][no-clear]:not([readonly]):not([condensed])) input {
    padding-right: 6.5rem;
  }
  :host([success][no-clear]:not([readonly]):not([condensed])) input {
    padding-right: 2.5rem;
  }
  :host([success][no-clear]:not([readonly]):not([no-arrows]):not([condensed])) input {
    padding-right: 6.5rem;
  }
  :host([error][no-clear]:not([readonly]):not([no-arrows]):not([condensed])) input {
    padding-right: 6.5rem;
  }

  :host(:hover[error]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]):not([no-clear]))
    input,
  :host(:hover[success]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]):not([no-clear]))
    input,
  :host(:focus-within[success]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]):not([no-clear]))
    input,
  :host([error]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]):not([no-clear]))
    input {
    padding-right: 8.5rem;
  }
  :host([success]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]):not([no-clear]))
    input {
    padding-right: 6.5rem;
  }
  :host([readonly]:not([error]):not([success])) .input-number-label,
  :host(:focus-within[readonly]:not([error]):not([success])) .input-number-label,
  :host(:focus[readonly]:not([error]):not([success])) .input-number-label {
    width: calc(100% - 1rem);
  }
  /* focus */
  input:focus:not([error]),
  input:focus:not([success]),
  input:focus:not([safety]) {
    border-color: rgba(var(--ui-1), var(--opacity-2));
  }
  input::-ms-clear {
    display: none;
  }
  input:focus {
    outline: none;
  }
  :host(:not([condensed]):not([readonly])) input:focus + .input-number-label {
    top: 0.25rem;
    font: var(--body-2);
  }

  :host([readonly]) input:focus + .input-number-label {
    color: var(--text-secondary);
  }
  :host([keypad-active]) .input-number-label {
    top: 0.25rem;
    font: var(--body-2);
  }
  :host([disabled]) input {
    cursor: default;
    resize: none;
    pointer-events: none;
  }
  :host([readonly]) input {
    cursor: default;
    pointer-events: none;
    background: transparent;
  }
  .content-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
  }
  .icon-wrapper {
    position: absolute;
    top: 0.5rem;
    right: 4.5rem;
  }
  :host([readonly]) .icon-wrapper {
    right: 0.5rem;
  }
  /*condensed*/
  :host([condensed]) .input-number-label {
    display: none;
  }
  :host(.empty[condensed]) .input-number-label {
    display: flex;
    top: 0.25rem;
    font: var(--body-1);
    line-height: 1.5rem;
  }
  :host([condensed]) input,
  :host([condensed]) input:focus,
  :host([condensed][no-clear]) input,
  :host([condensed][no-arrows]) input,
  :host([condensed][no-clear][no-arrows]) input {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    height: 2rem;
  }
  :host([condensed]:not([readonly])) input:focus + .input-number-label {
    display: none;
  }
  :host([condensed]) #plus,
  :host([condensed]) #minus {
    margin-top: -0.25rem;
  }
  :host([condensed]) .icon-wrapper,
  :host([condensed]) #clear {
    top: 0.25rem;
  }
  :host([disabled]) .input-number-label,
  :host([disabled]) input {
    color: var(--text-disabled);
  }
  /* Clear Field Icon */
  #clear {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    transition: var(--transition-time) all ease-in-out;
    cursor: pointer;
    right: 4.5rem;
    top: 0.5rem;
  }
  :host([readonly]) #clear,
  :host([disabled]) #clear,
  :host([no-clear]) #clear,
  :host(.empty) #clear {
    display: none !important;
  }
  :host([condensed][no-clear]) input {
    padding-top: 0.25rem;
  }
  :host([condensed][no-clear][no-arrows]) input {
    padding-top: 0.25rem;
  }
  :host([error]:not([no-arrows])) #clear,
  :host([success]:not([no-arrows])) #clear {
    right: 6.5rem;
  }
  /* Validation Styles */
  #mandatory {
    display: none;
    color: rgba(var(--ui-2), var(--opacity-1));
  }
  :host([error]) input,
  :host([error][readonly]) input {
    border-color: rgba(var(--functional-red), var(--opacity-1));
  }
  :host([success]) input,
  :host([success][readonly]) input {
    border-color: rgba(var(--functional-green), var(--opacity-1));
  }
  :host([safety]) input,
  :host([safety][readonly]) input {
    border-color: rgba(var(--functional-yellow), var(--opacity-2));
  }
  :host([mandatory]) #mandatory {
    display: inline-block;
  }
  :host([disabled]) #mandatory {
    color: rgba(var(--ui-2), var(--opacity-5));
  }
  /* Stepper Styles */
  input::-webkit-inner-spin-button {
    display: none;
  }
  .icon-slot {
    display: flex;
    flex-direction: row;
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
  }
  :host([readonly]) .icon-slot {
    display: none;
  }
  :host([disabled]) .icon-slot > * {
    pointer-events: none;
  }
  /* hover */
  :host(.empty:not(.touch-device)) input:hover:not(:focus),
  :host(.empty.touch-device) input:hover:not(:focus) {
    background: rgba(var(--ui-1), var(--opacity-6)) !important;
  }
  :host(:not([readonly]):not(.touch-device)) input:hover:not(:focus),
  :host(:not([readonly]).touch-device) input:hover:not(:focus) {
    background: rgba(var(--ui-1), var(--opacity-7));
  }
  :host(:hover:not(.empty)) #clear,
  :host(:focus:not(.empty)) #clear,
  :host([error]:not(.empty)) #clear,
  #clear:focus {
    opacity: 1;
    pointer-events: all;
  }
  input:invalid ~ .input-number-label {
    top: 0.25rem;
    font: var(--body-2);
    line-height: 1rem;
  }
  input:invalid {
    padding: 1.25rem 6.5rem 0.25rem 0.5rem;
  }
  :host(:hover) input:invalid ~ #clear {
    opacity: 1 !important;
    pointer-events: all !important;
    display: block !important;
  }
  :host input:hover {
    background: rgba(var(--ui-1), var(--opacity-6)) !important;
  }
  :host(:hover) #clear {
    opacity: 1 !important;
  }
  :host(.empty:not(.touch-device)) input:hover:not(:focus),
  :host(.empty.touch-device) input:hover:not(:focus),
  :host(.empty:not(.touch-device)) input.hover:not(:focus),
  :host(.empty.touch-device) input.hover:not(:focus) {
    background: rgba(var(--ui-1), var(--opacity-6));
  }
  :host(:not([readonly]):not(.touch-device)) input:hover:not(:focus),
  :host(:not([readonly]).touch-device) input:hover:not(:focus),
  :host(:not([readonly]):not(.touch-device)) input.hover:not(:focus),
  :host(:not([readonly]).touch-device) input.hover:not(:focus) {
    background: rgba(var(--ui-1), var(--opacity-7));
  }
  :host(:hover) input:invalid ~ #clear,
  :host(.hover) input:invalid ~ #clear {
    opacity: 1 !important;
    pointer-events: all !important;
    display: block !important;
  }
  :host input:hover,
  :host input.hover {
    background: rgba(var(--ui-1), var(--opacity-6)) !important;
  }

  /* helper text */
  .helper-text {
    padding: 0.25rem 0.5rem 0rem 0.5rem;
    box-sizing: border-box;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  :host(:not([error])[error-message]) .error-message {
    display: none;
  }
  :host([error][hint][error-message]) .hint {
    display: none;
  }
  :host([disabled]) .helper-text {
    color: var(--text-disabled);
  }
  :host(:not([error])) .error-icon,
  :host(:not([success])) .success-icon {
    display: none;
  }
  :host([error]:not([readonly])) .input-number-label,
  :host([success]:not([readonly])) .input-number-label,
  :host(:hover:not(.empty):not([disabled]):not([readonly])) .input-number-label,
  :host(:focus:not(.empty):not([disabled]):not([readonly])) .input-number-label,
  :host(:focus-within) .input-number-label {
    width: calc(100% - 6.5rem);
  }
  :host([error][readonly]) .input-number-label,
  :host([success][readonly]) .input-number-label {
    width: calc(100% - 3rem);
  }
  :host(:hover[error]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]))
    .input-number-label,
  :host(:hover[success]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]))
    .input-number-label,
  :host(:focus-within[success]:not(.empty):not([disabled]):not([readonly]):not([no-arrows]))
    .input-number-label,
  :host([error]:not(.empty):not([disabled]):not([readonly]):not([no-arrows])) .input-number-label {
    width: calc(100% - 9rem);
  }
  .items-wrapper {
    max-height: 12.5rem;
    min-width: 0;
    overflow: auto;
    padding: 0 0.5rem;
    background: rgba(var(--ui-8), var(--opacity-1));
    width: 13rem;
    position: fixed;
  }
  :host([no-border]) input {
    border-bottom: none;
  }
  /* no clear styles */
  :host([no-clear]):not([condensed]) input,
  :host(:hover:not(.empty):not([condensed]):not([disabled]):not([readonly])[no-clear]) input {
    padding: 1rem 6.5rem 0rem 0.5rem;
  }
  :host(:hover:not(.empty):not([disabled]):not([readonly])[no-clear]) .input-number-label,
  :host(:focus:not(.empty):not([disabled]):not([readonly])[no-clear]) .input-number-label,
  :host(:not([condensed]):not(.empty)[no-clear]) input:focus + .input-number-label {
    width: calc(100% - 4.5rem);
  }
  /* no arrow styles */
  :host([no-arrows]) input {
    padding: 1rem 0.25rem 0rem 0.5rem;
  }
  :host(:hover:not(.empty):not([condensed]):not([disabled]):not([readonly])[no-arrows][no-clear]:not([error]):not([success]))
    input {
    padding: 1rem 0.25rem 0rem 0.5rem;
  }
  :host(:hover:not(.empty):not([condensed]):not([disabled]):not([readonly])[no-arrows]:not([error]):not([success]))
    input {
    padding: 1rem 2.25rem 0rem 0.5rem;
  }

  :host(:focus:not(.empty):not([disabled]):not([readonly])[no-arrows]) .input-number-label,
  :host(:not([condensed]):not(.empty)[no-arrows]) input:focus + .input-number-label {
    width: calc(100% - 4.5rem);
  }

  :host(:focus:not(.empty):not([disabled]):not([readonly])[no-arrows][no-clear]:not([success]):not([error]))
    .input-number-label {
    width: calc(100% - 0.5rem);
  }
  :host(:focus:not(.empty):not([no-arrows]):not([disabled]):not([readonly])) .input-number-label {
    width: calc(100% - 1.5rem);
  }
  :host(:focus:not(.empty):not([no-arrows]):not([disabled]):not([readonly]):not([no-clear]))
    .input-number-label {
    width: calc(100% - 6.5rem);
  }
  :host([no-arrows]) .input-number-label,
  :host(:hover:not(.empty):not([disabled]):not([readonly])[no-arrows]:not([error]):not([success]))
    .input-number-label {
    width: calc(100% - 0.5rem);
  }
  :host([no-arrows]:not([no-clear])) .input-number-label,
  :host(:hover:not(.empty):not([no-clear]):not([disabled]):not([readonly])[no-arrows]:not([error]):not([success]))
    .input-number-label {
    width: calc(100% - 1.5rem);
  }
  :host([no-clear][no-arrows]):not([condensed]) input,
  :host(:hover:not(.empty):not([condensed]):not([disabled]):not([readonly])[no-clear][no-arrows])
    input {
    padding: 1rem 6.5rem 0rem 0.5rem;
  }

  :host([no-clear][no-arrows][success]):not([condensed]) input,
  :host(:hover:not(.empty):not([condensed]):not([disabled]):not([readonly])[no-clear][no-arrows][success])
    input {
    padding: 1rem 2.5rem 0rem 0.5rem;
  }
  :host([no-clear][no-arrows][error]):not([condensed]) input,
  :host(:hover:not(.empty):not([condensed]):not([disabled]):not([readonly])[no-clear][no-arrows][error])
    input {
    padding: 1rem 2.5rem 0rem 0.5rem;
  }

  :host(:hover:not(.empty):not([disabled]):not([readonly])[no-arrows][no-clear])
    .input-number-label,
  :host(:not([condensed]):not(.empty)[no-clear][no-arrows]) input:focus + .input-number-label {
    width: calc(100% - 2.25rem);
  }
  :host(:not([condensed]):not(.empty)[no-clear][no-arrows][no-clear])
    input:focus
    + .input-number-label {
    width: calc(100% - 0.5rem);
  }

  :host([error]:not([condensed]):not(.empty)[no-clear][no-arrows][no-clear])
    input:focus
    + .input-number-label {
    width: calc(100% - 2.25rem);
  }
  :host([success]:not([condensed]):not(.empty)[no-clear][no-arrows][no-clear])
    input:focus
    + .input-number-label {
    width: calc(100% - 2.25rem);
  }

  :host([no-clear][no-arrows]) .input-number-label {
    width: calc(100% - 1rem);
  }
  :host([no-clear][no-arrows][success]) .input-number-label {
    width: calc(100% - 2.5rem);
  }
  :host([no-clear][no-arrows][error]) .input-number-label {
    width: calc(100% - 2.5rem);
  }
  /* error and success conditions' styles when no-arrows applied */
  :host([no-arrows][error]) .icon-wrapper,
  :host([no-arrows][success]) .icon-wrapper {
    position: absolute;
    right: 0.25rem;
  }
  :host(:hover[no-arrows][error]:not([readonly]):not([disabled])) #clear,
  :host(:hover[no-arrows][success]:not([readonly]):not([disabled])) #clear,
  :host([no-arrows][error]:not([readonly]):not([disabled])) #clear,
  :host([no-arrows][success]:not([readonly]):not([disabled])) #clear {
    position: absolute;
    right: 2.25rem;
  }
  :host([no-arrows][success]) input {
    padding-right: 2.25rem;
  }
  :host([no-arrows][success]:not([no-clear])) input {
    padding-right: 4.25rem;
  }
  :host(:hover[no-arrows][success]):not([no-clear]) input {
    padding-right: 4.25rem;
  }
  :host([no-arrows][error]) input,
  :host(:hover[no-arrows][error]):not([readonly]):not([disabled]) input,
  :host(:hover[no-arrows][success]):not([readonly]):not([disabled]) input {
    padding-right: 4.25rem;
  }
  :host([no-arrows][success]:not([no-clear])) .input-number-label {
    width: calc(100% - 4.5rem);
  }
  :host([no-arrows][error]:not([no-clear])) .input-number-label,
  :host(:hover[no-arrows][error]:not([no-clear])) .input-number-label,
  :host([no-arrows][error]:not([no-clear]):not(.empty)) input:focus + .input-number-label,
  :host(:hover[no-arrows][success]:not([no-clear]):not(.empty)) input:focus + .input-number-label,
  :host(:hover[no-arrows][success]:not([no-clear]):not(.empty):not([readonly]):not([disabled]))
    .input-number-label {
    width: calc(100% - 4.75rem);
  }
  :host(:not([condensed]):not(.empty)) input:focus + .input-number-label {
    width: calc(100% - 7rem);
  }
`;
